/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaBan,
  FaCheckCircle,
  FaDownload,
  FaEllipsisV,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFilm,
  FaGlobe,
  FaHourglassHalf,
  FaImage,
  FaMusic,
  FaPause,
  FaPhone,
  FaPlay,
  FaReply,
} from 'react-icons/fa';
import { IconBaseProps, IconType } from 'react-icons';
import './index.css';

type TemplateType = 'text' | 'media' | 'call_to_action' | 'quick_reply';
type MediaType = 'image' | 'video' | 'audio' | 'document';
type MediaModerationStatus = 'approved' | 'pending' | 'rejected';
type ButtonType = 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL' | string;
type LegacyCompatibleIcon = (props: IconBaseProps) => JSX.Element;

export interface WhatsappPreviewButton {
  id: string;
  text: string;
  type: ButtonType;
}

export interface WhatsappPreviewProps {
  companyName?: string;
  templateType: TemplateType;
  bodyText: string;
  mediaUrl?: string;
  mediaType?: MediaType;
  mediaFileName?: string;
  mediaModerationStatus?: MediaModerationStatus;
  buttons?: WhatsappPreviewButton[];
}

const audioThresholds = [
  6.67,
  13.34,
  20,
  26.67,
  33.34,
  40,
  46.67,
  53.34,
  60,
  66.67,
  73.34,
  80,
  86.67,
  93.34,
  100,
];

function formatTime(value: number) {
  if (!Number.isFinite(value) || value < 0) {
    return '0:00';
  }

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60)
    .toString()
    .padStart(2, '0');

  return `${minutes}:${seconds}`;
}

function getDocumentType(mediaFileName?: string) {
  if (!mediaFileName || !mediaFileName.includes('.')) {
    return 'DOC';
  }

  return mediaFileName
    .split('.')
    .pop()!
    .toUpperCase();
}

function Icon({
  icon: IconComponent,
  ...props
}: IconBaseProps & { icon: IconType }) {
  const CompatibleIcon = (IconComponent as unknown) as LegacyCompatibleIcon;

  return <CompatibleIcon {...props} />;
}

function getButtonIcon(type: ButtonType) {
  switch (type) {
    case 'QUICK_REPLY':
      return <Icon icon={FaReply} aria-hidden="true" />;
    case 'PHONE_NUMBER':
      return <Icon icon={FaPhone} aria-hidden="true" />;
    case 'URL':
      return <Icon icon={FaExternalLinkAlt} aria-hidden="true" />;
    default:
      return <Icon icon={FaGlobe} aria-hidden="true" />;
  }
}

export default function WhatsappPreview({
  companyName = 'Your Company',
  templateType = 'text',
  bodyText,
  mediaUrl,
  mediaType = 'image',
  mediaFileName,
  mediaModerationStatus = 'approved',
  buttons = [],
}: WhatsappPreviewProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState('');
  const [audioDuration, setAudioDuration] = useState('0:00');
  const [audioProgress, setAudioProgress] = useState(0);

  useEffect(() => {
    setImageLoadError(false);
    setImageLoading(Boolean(mediaUrl) && mediaType === 'image');
    setVideoLoadError(false);
    setAudioLoadError(false);
    setVideoPlaying(false);
    setAudioPlaying(false);
    setVideoDuration('');
    setAudioDuration('0:00');
    setAudioProgress(0);

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [mediaUrl, mediaType, mediaModerationStatus]);

  const showMedia =
    templateType === 'media' && mediaModerationStatus === 'approved';

  const audioBars = useMemo(
    () =>
      audioThresholds.map((threshold, index) => ({
        threshold,
        key: `bar-${index}`,
      })),
    []
  );

  const handlePlayVideo = async () => {
    if (!videoRef.current) {
      return;
    }

    try {
      await videoRef.current.play();
      setVideoPlaying(true);
    } catch {
      setVideoLoadError(true);
    }
  };

  const handleToggleAudio = async () => {
    if (!audioRef.current) {
      return;
    }

    try {
      if (audioRef.current.paused) {
        await audioRef.current.play();
        setAudioPlaying(true);
        return;
      }

      audioRef.current.pause();
      setAudioPlaying(false);
    } catch {
      setAudioLoadError(true);
    }
  };

  const handleSeekAudio = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !audioRef.current.duration) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const offset = Math.min(
      Math.max(event.clientX - bounds.left, 0),
      bounds.width
    );
    const percentage = offset / bounds.width;
    audioRef.current.currentTime = percentage * audioRef.current.duration;
    setAudioProgress(percentage * 100);
  };

  return (
    <div className="whatsapp-preview">
      <header className="chat-header">
        <div className="header-left">
          <Icon icon={FaArrowLeft} aria-hidden="true" className="header-icon" />
          <div className="avatar-placeholder" />
        </div>

        <div className="header-center">
          <span className="company-name">{companyName}</span>
          <Icon
            icon={FaCheckCircle}
            aria-hidden="true"
            className="header-verified"
          />
        </div>

        <div className="header-right">
          <Icon icon={FaPhone} aria-hidden="true" className="header-icon" />
          <Icon
            icon={FaEllipsisV}
            aria-hidden="true"
            className="header-icon menu-icon"
          />
        </div>
      </header>

      <div className="preview-content">
        <div className="message-bubble">
          {templateType !== 'media' ? (
            <div className={templateType}>
              <div className="message">
                {bodyText}
                <div className="time">12:34</div>
              </div>
            </div>
          ) : (
            <div className={templateType}>
              {mediaModerationStatus === 'rejected' && (
                <div className="media-denied-placeholder">
                  <div className="media-denied-icon">
                    <Icon icon={FaBan} aria-hidden="true" />
                  </div>
                  <span className="media-denied-label">File Rejected</span>
                  <span className="media-denied-sub">
                    This file was removed by moderation
                  </span>
                </div>
              )}

              {mediaModerationStatus === 'pending' && (
                <div className="media-pending-placeholder">
                  <div className="media-pending-icon">
                    <Icon icon={FaHourglassHalf} aria-hidden="true" />
                  </div>
                  <span className="media-pending-label">Under Review</span>
                  <span className="media-pending-sub">
                    Your file is being reviewed
                  </span>
                </div>
              )}

              {showMedia && mediaType === 'image' && (
                <div className="whatsapp-image-preview">
                  {!imageLoadError && mediaUrl ? (
                    <>
                      <img
                        src={mediaUrl}
                        alt="Media preview"
                        className={imageLoading ? 'image-loading' : ''}
                        style={{ display: imageLoading ? 'none' : 'block' }}
                        onLoad={() => setImageLoading(false)}
                        onError={() => {
                          setImageLoadError(true);
                          setImageLoading(false);
                        }}
                      />
                      {imageLoading && (
                        <div
                          className="image-loader"
                          aria-label="Loading image">
                          <span className="image-loader-spinner" />
                          <span>Loading image...</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="media-placeholder">
                      <Icon icon={FaImage} aria-hidden="true" />
                      <span>Image Preview</span>
                    </div>
                  )}
                </div>
              )}

              {showMedia && mediaType === 'video' && (
                <div className="whatsapp-video-preview">
                  {!videoLoadError && mediaUrl ? (
                    <div className="video-thumbnail">
                      <video
                        ref={videoRef}
                        className="video-element"
                        preload="metadata"
                        controls
                        onError={() => setVideoLoadError(true)}
                        onPlay={() => setVideoPlaying(true)}
                        onPause={() => setVideoPlaying(false)}
                        onLoadedMetadata={event => {
                          setVideoDuration(
                            formatTime(event.currentTarget.duration)
                          );
                        }}>
                        <source src={mediaUrl} type="video/mp4" />
                      </video>
                      {!videoPlaying && (
                        <div
                          className="video-overlay"
                          onClick={handlePlayVideo}>
                          <button
                            type="button"
                            className="play-button"
                            aria-label="Play video preview">
                            <Icon icon={FaPlay} aria-hidden="true" />
                          </button>
                          {videoDuration ? (
                            <div className="video-duration">
                              {videoDuration}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="media-placeholder">
                      <Icon icon={FaFilm} aria-hidden="true" />
                      <span>Video Preview</span>
                    </div>
                  )}
                </div>
              )}

              {showMedia && mediaType === 'audio' && (
                <div className="whatsapp-audio-preview">
                  <audio
                    ref={audioRef}
                    className="sr-only"
                    onError={() => setAudioLoadError(true)}
                    onLoadedMetadata={event => {
                      setAudioDuration(
                        formatTime(event.currentTarget.duration)
                      );
                    }}
                    onTimeUpdate={event => {
                      const currentAudio = event.currentTarget;
                      const nextProgress = currentAudio.duration
                        ? (currentAudio.currentTime / currentAudio.duration) *
                          100
                        : 0;
                      setAudioProgress(nextProgress);
                    }}
                    onPlay={() => setAudioPlaying(true)}
                    onPause={() => setAudioPlaying(false)}
                    onEnded={() => {
                      setAudioPlaying(false);
                      setAudioProgress(0);
                    }}>
                    <source src={mediaUrl} type="audio/mpeg" />
                  </audio>

                  {!audioLoadError && mediaUrl ? (
                    <div className="audio-container">
                      <button
                        type="button"
                        className="audio-play-button"
                        onClick={handleToggleAudio}
                        aria-label={
                          audioPlaying
                            ? 'Pause audio preview'
                            : 'Play audio preview'
                        }>
                        {audioPlaying ? (
                          <Icon icon={FaPause} aria-hidden="true" />
                        ) : (
                          <Icon icon={FaPlay} aria-hidden="true" />
                        )}
                      </button>
                      <div className="audio-waveform" onClick={handleSeekAudio}>
                        <div className="waveform-bars">
                          {audioBars.map(({ key, threshold }) => (
                            <span
                              key={key}
                              className={
                                audioProgress >= threshold
                                  ? 'bar active'
                                  : 'bar'
                              }
                            />
                          ))}
                        </div>
                      </div>
                      <div className="audio-duration">{audioDuration}</div>
                    </div>
                  ) : (
                    <div className="media-placeholder audio-fallback">
                      <div className="audio-fallback-content">
                        <Icon icon={FaMusic} aria-hidden="true" />
                        <span>Audio Preview</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showMedia && mediaType === 'document' && (
                <div className="whatsapp-document-preview">
                  <div className="document-container">
                    <div className="document-icon">
                      <Icon icon={FaFileAlt} aria-hidden="true" />
                    </div>
                    <div className="document-info">
                      <div className="document-name">
                        {mediaFileName || 'Document.pdf'}
                      </div>
                      <div className="document-meta">
                        <span className="document-type">
                          {getDocumentType(mediaFileName)}
                        </span>
                        <span className="document-separator">•</span>
                        <span className="document-size">1 page</span>
                      </div>
                    </div>
                    <div className="document-download">
                      <Icon icon={FaDownload} aria-hidden="true" />
                    </div>
                  </div>
                </div>
              )}

              <div className="message">
                {bodyText}
                <div className="time">12:34</div>
              </div>
            </div>
          )}

          {buttons.length > 0 && (
            <div>
              {buttons.map(button => (
                <button
                  key={button.id}
                  type="button"
                  className="preview-button">
                  {getButtonIcon(button.type)}
                  <span>{button.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
